import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BookMarked, Plus, Search, Trash2, Pencil, ShieldCheck, Eye, Wrench, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, StatTile } from "@/components/common/Panel";
import { TiltCard } from "@/components/common/TiltCard";
import { ModuleGate } from "@/components/common/ModuleGate";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const CATEGORY_TONE = {
    preventive: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    detective: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
    corrective: "text-amber-300 border-amber-500/30 bg-amber-500/10",
};
const CATEGORY_ICON = { preventive: ShieldCheck, detective: Eye, corrective: Wrench };

const emptyForm = { control_id: "", title: "", description: "", category: "preventive", control_type: "manual", tags: "", implementation_guidance: "" };

export default function MitigationLibrary() {
    const [controls, setControls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("all");
    const [tagFilter, setTagFilter] = useState("all");
    const [expanded, setExpanded] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/governance/mitigation-controls");
            setControls(data);
        } catch (e) {
            console.warn("Failed to load mitigation controls", e);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const allTags = useMemo(() => Array.from(new Set(controls.flatMap((c) => c.tags))).sort(), [controls]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return controls.filter((c) => {
            if (category !== "all" && c.category !== category) return false;
            if (tagFilter !== "all" && !c.tags.includes(tagFilter)) return false;
            if (q && !(c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.control_id.toLowerCase().includes(q))) return false;
            return true;
        });
    }, [controls, search, category, tagFilter]);

    const stats = useMemo(() => ({
        total: controls.length,
        preventive: controls.filter((c) => c.category === "preventive").length,
        detective: controls.filter((c) => c.category === "detective").length,
        corrective: controls.filter((c) => c.category === "corrective").length,
    }), [controls]);

    const openCreate = () => { setForm(emptyForm); setEditingId(null); setFormOpen(true); };
    const openEdit = (c) => {
        setForm({ control_id: c.control_id, title: c.title, description: c.description, category: c.category, control_type: c.control_type, tags: c.tags.join(", "), implementation_guidance: c.implementation_guidance });
        setEditingId(c.id);
        setFormOpen(true);
    };

    const submit = async () => {
        if (!form.control_id.trim() || !form.title.trim() || !form.description.trim()) {
            toast.error("Control ID, title and description are required"); return;
        }
        setSaving(true);
        const payload = {
            control_id: form.control_id.trim(),
            title: form.title.trim(),
            description: form.description.trim(),
            category: form.category,
            control_type: form.control_type,
            tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            implementation_guidance: form.implementation_guidance.trim(),
        };
        try {
            if (editingId) {
                await api.put(`/governance/mitigation-controls/${editingId}`, payload);
                toast.success("Control updated");
            } else {
                await api.post("/governance/mitigation-controls", payload);
                toast.success("Control added to the library");
            }
            setFormOpen(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const remove = async (c) => {
        try {
            await api.delete(`/governance/mitigation-controls/${c.id}`);
            setControls((prev) => prev.filter((x) => x.id !== c.id));
            toast.success("Control removed");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <ModuleGate module="governance" label="Governance — Mitigation Library">
        <div className="space-y-6">
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-center justify-between gap-4 relative flex-wrap">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                            <BookMarked size={22} className="text-slate-950" strokeWidth={2.5} />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-white tracking-wide">Mitigation Control Library</h1>
                            <p className="text-sm text-slate-400 mt-0.5">
                                Rule-based tag matching surfaces these controls instantly on SoD conflicts &amp; critical activities
                            </p>
                        </div>
                    </div>
                    <button data-testid={TID.mitigationAddBtn} onClick={openCreate} className="btn-primary sheen inline-flex items-center gap-2">
                        <Plus size={15} /> Add Control
                    </button>
                </div>
            </div>

            <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div variants={staggerItem}><StatTile testId={TID.statTile("mc-total")} label="Total Controls" value={stats.total} sub="in library" icon={BookMarked} tone="cyan" /></motion.div>
                <motion.div variants={staggerItem}><StatTile testId={TID.statTile("mc-preventive")} label="Preventive" value={stats.preventive} sub="stop before it happens" icon={ShieldCheck} tone="green" /></motion.div>
                <motion.div variants={staggerItem}><StatTile testId={TID.statTile("mc-detective")} label="Detective" value={stats.detective} sub="catch after the fact" icon={Eye} tone="cyan" /></motion.div>
                <motion.div variants={staggerItem}><StatTile testId={TID.statTile("mc-corrective")} label="Corrective" value={stats.corrective} sub="fix the design" icon={Wrench} tone="amber" /></motion.div>
            </motion.div>

            <Panel eyebrow="Library" title={`Mitigation controls (${filtered.length})`}>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid={TID.mitigationSearchInput}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search control id, title or description…"
                            className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                        />
                    </div>
                    <select data-testid={TID.mitigationCategoryFilter} value={category} onChange={(e) => setCategory(e.target.value)} className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none">
                        <option value="all">All categories</option>
                        <option value="preventive">Preventive</option>
                        <option value="detective">Detective</option>
                        <option value="corrective">Corrective</option>
                    </select>
                    <select data-testid={TID.mitigationTagFilter} value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none">
                        <option value="all">All tags</option>
                        {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>

                {loading ? (
                    <p className="text-sm text-slate-500 font-mono py-6 text-center">Loading library…</p>
                ) : filtered.length === 0 ? (
                    <p className="text-sm text-slate-500 font-mono py-6 text-center">No controls match your filters.</p>
                ) : (
                    <div className="grid md:grid-cols-2 gap-3">
                        {filtered.map((c) => {
                            const Icon = CATEGORY_ICON[c.category] || ShieldCheck;
                            const isOpen = expanded === c.id;
                            return (
                                <TiltCard key={c.id} max={2} testId={TID.mitigationCard(c.id)} className="panel card-lift p-4 rounded-[4px]">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(isOpen ? null : c.id)}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono text-[11px] text-cyan-300">{c.control_id}</span>
                                                <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${CATEGORY_TONE[c.category]}`}>
                                                    <Icon size={10} /> {c.category}
                                                </span>
                                                <span className="text-[10px] font-mono text-slate-500 uppercase">{c.control_type}</span>
                                            </div>
                                            <div className="text-sm text-white font-medium mt-1">{c.title}</div>
                                            <p className="text-xs text-slate-400 mt-1 leading-relaxed">{c.description}</p>
                                            {isOpen && (
                                                <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                                                    {c.implementation_guidance && (
                                                        <div>
                                                            <div className="label-eyebrow mb-1">Implementation Guidance</div>
                                                            <p className="text-[11px] text-slate-300 leading-relaxed">{c.implementation_guidance}</p>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap gap-1">
                                                        {c.tags.map((t) => (
                                                            <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">{t}</span>
                                                        ))}
                                                    </div>
                                                    {c.framework_refs.length > 0 && (
                                                        <div className="flex flex-wrap gap-1">
                                                            {c.framework_refs.map((f) => (
                                                                <span key={f} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">{f}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2 shrink-0">
                                            <button data-testid={TID.mitigationEditBtn(c.id)} onClick={() => openEdit(c)} className="text-slate-500 hover:text-cyan-400 transition-colors" aria-label="Edit control">
                                                <Pencil size={14} />
                                            </button>
                                            <button data-testid={TID.mitigationDeleteBtn(c.id)} onClick={() => remove(c)} className="text-slate-500 hover:text-rose-400 transition-colors" aria-label="Delete control">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </TiltCard>
                            );
                        })}
                    </div>
                )}
            </Panel>

            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-white">{editingId ? "Edit Mitigation Control" : "Add Mitigation Control"}</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500 font-mono">
                            Compensating control used to mitigate an SoD risk when segregation is not achievable.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Control ID" value={form.control_id} onChange={(v) => setForm({ ...form, control_id: v })} placeholder="MC-029" disabled={!!editingId} />
                            <FormField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Quarterly Access Review" />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1">Description</label>
                            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="field w-full resize-y" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-eyebrow block mb-1">Category</label>
                                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="field w-full">
                                    <option value="preventive">Preventive</option>
                                    <option value="detective">Detective</option>
                                    <option value="corrective">Corrective</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1">Control Type</label>
                                <select value={form.control_type} onChange={(e) => setForm({ ...form, control_type: e.target.value })} className="field w-full">
                                    <option value="manual">Manual</option>
                                    <option value="automated">Automated</option>
                                    <option value="it-dependent">IT-Dependent</option>
                                </select>
                            </div>
                        </div>
                        <FormField label="Tags (comma-separated)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="sod, finance, critical" />
                        <div>
                            <label className="label-eyebrow block mb-1">Implementation Guidance</label>
                            <textarea value={form.implementation_guidance} onChange={(e) => setForm({ ...form, implementation_guidance: e.target.value })} rows={2} className="field w-full resize-y" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button data-testid={TID.mitigationFormSubmit} onClick={submit} disabled={saving} className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                            {editingId ? "Save Changes" : "Add Control"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </ModuleGate>
    );
}

function FormField({ label, value, onChange, placeholder, disabled }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1">{label}</label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className="field w-full disabled:opacity-50"
            />
        </div>
    );
}
